import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { useDeviceLayout } from '@vio/atlas/context/deviceLayout';
import { getAnonymousId } from '@daedalus/core/src/_web/anonymousId';
import { useBrand } from '@daedalus/core/src/_web/brand/hooks/useBrand';
import getBookingContext from '@daedalus/core/src/analytics/contexts/BookingContext';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { customerIo } from '@daedalus/core/src/analytics/services/CustomerIo';
import { Action, AnalyticsContext, BaseOfferContext, Category, Entity, Page, Team } from '@daedalus/core/src/analytics/types/Events';
import { selectUserId } from '@daedalus/core/src/auth/modules/selectors';
import { getLinkByType } from '@daedalus/core/src/offer/business/getLinkByType';
import { ActionLinkTypes } from '@daedalus/core/src/offer/types/actionLink';
import PriceTypes from '@daedalus/core/src/offer/types/PriceTypes';
import { getDisplayPrice, getNightlyPrice, getPriceByType } from '@daedalus/core/src/price/business/price';
import { numberOfGuests } from '@daedalus/core/src/room/business/roomConfiguration';
import { getTimezone } from '@daedalus/core/src/utils/date';
import { useFrozenDealToast } from '../../hooks/useFrozenDealToast';
import { getAnalyticsContext, getApplication, getBookingStatus, getComponent, getError, getFreezeSelectedContext, getIsFreezeConfirmationOverlayOpen, getOfferEligibility } from '../../modules/selector';
import { closeFreezeConfirmationOverlay } from '../../modules/slice';
import { setUserAlreadyFrozeADealCookie } from '../../services/cookies';
import { useCheckUserLimitQuery, useGetFrozenBookingsQuery, useRetrieveBookingQuery } from '../../services/dealFreezeApi';
import { getDayDistance } from '../../utils/date';
import { findRoomByOfferId } from '../../utils/offer';
import { getBookingManagementUrl, getDealFreezePaymentUrl, getMyBookingsUrl } from '../../utils/url';
import { hasRemainingMessage } from '../../utils/userLimits';
import { FreezeOverlay } from '../FreezeOverlay';
import { ConfirmationContent } from './ConfirmationContent';
import { CtaButton } from './CtaButton';
const OverlayWrapper = /*#__PURE__*/styled('div')({
  name: "OverlayWrapper",
  class: "o1mf79w0",
  propsAsIs: false
});
export const FreezeConfirmationOverlay: React.FC = () => {
  const {
    isMobile
  } = useDeviceLayout();
  const {
    response: offerCheckResponse
  } = useSelector(getOfferEligibility);
  const {
    errored
  } = useSelector(getError);
  const {
    links
  } = useSelector(getBookingStatus) || {};
  const dispatch = useDispatch();
  const isOpen = useSelector(getIsFreezeConfirmationOverlayOpen);
  const [trackedBookingIds, setTrackedBookingIds] = useState<string[]>([]);
  const {
    hotel,
    searchDetail,
    sapiMatchedOffer,
    rooms
  } = useSelector(getFreezeSelectedContext);
  const {
    bookingManagementUrl: myBookingUrl = '',
    checkoutUrl
  } = useSelector(getApplication);
  const component = useSelector(getComponent);
  const {
    brand
  } = useBrand();
  const cio = customerIo(brand);
  const userId = useSelector(selectUserId);
  const {
    leadContext,
    offerContext
  } = useSelector(getAnalyticsContext);
  const anonymousId = getAnonymousId();
  const retrieveBookingLink = getLinkByType(links, ActionLinkTypes.BOOKING_RETRIEVE);
  const shouldSkipRetrieve = !retrieveBookingLink || !isOpen;
  const retrieveBookingResult = useRetrieveBookingQuery({
    url: retrieveBookingLink
  }, {
    skip: shouldSkipRetrieve
  });
  const myBookingsUrl = getMyBookingsUrl({
    hostUrl: myBookingUrl,
    anonymousId
  });
  const {
    showFrozenDealToast
  } = useFrozenDealToast({
    myBookingsUrl
  });

  /**
   * Retrieve and update store's frozen booking data to toggle between "Freeze deal" and "Manage freeze" buttons, and
   * also checking if user is still eligible for Deal Freeze (they may have reached the limit)
   */
  useGetFrozenBookingsQuery({}, {
    skip: shouldSkipRetrieve,
    refetchOnMountOrArgChange: true
  });
  const {
    data
  } = useCheckUserLimitQuery({}, {
    skip: shouldSkipRetrieve,
    refetchOnMountOrArgChange: true
  });
  const {
    hotelName,
    starRating,
    imageURIs,
    displayAddress
  } = hotel || {};
  const {
    checkIn,
    checkOut,
    roomsSplit
  } = searchDetail || {};
  useEffect(() => {
    const bookingData = retrieveBookingResult?.data;
    const bookingId = bookingData?.id;
    if (retrieveBookingResult.isError || !bookingId || trackedBookingIds.includes(bookingId)) {
      return;
    }
    const bookingManagementUrl = myBookingUrl && getBookingManagementUrl({
      hostUrl: myBookingUrl,
      bookingId,
      anonymousId
    });
    const paymentUrl = checkoutUrl && getDealFreezePaymentUrl({
      hostUrl: checkoutUrl,
      bookingId
    });
    const numberOfNights = getDayDistance(checkIn, checkOut);
    const totalGuests = numberOfGuests(roomsSplit);
    const price = getDisplayPrice(bookingData.prices) || getPriceByType(PriceTypes.CHARGEABLE_CURRENCY, bookingData.prices);
    const nightlyPrice = getNightlyPrice(price?.chargeable.total, numberOfNights);
    dispatch(trackEvent({
      category: Category.System,
      entity: Entity.DealFreezeConfirmationOverlay,
      action: Action.Displayed,
      ...(component && {
        component
      }),
      analyticsContext: {
        [AnalyticsContext.BookingContext]: getBookingContext(bookingData),
        [AnalyticsContext.LeadContext]: leadContext,
        [AnalyticsContext.OfferContext]: offerContext as BaseOfferContext,
        [AnalyticsContext.MarketingAutomationContext]: {
          // Please ensure accuracy when modifying this object. Values are used in reminder emails.
          hotelName: hotelName,
          hotelImageUrl: imageURIs?.[0],
          bookingManagementUrl,
          paymentUrl,
          hotelAddress: displayAddress,
          price,
          numberOfNights,
          totalGuests,
          starRating,
          ...(nightlyPrice && {
            nightlyPrice
          })
        }
      },
      page: Page.Accommodation,
      team: Team.Select
    }));
    cio.trackEvent({
      name: `${Category.User}_${Entity.DealFreeze}_${Action.Succeeded}`,
      pageName: Page.Accommodation,
      userId,
      anonymousId: anonymousId,
      timezone: getTimezone(),
      hotelName: hotelName,
      hotelImageUrl: imageURIs?.[0],
      bookingData: {
        ...bookingData,
        bookingManagementUrl,
        paymentUrl
      },
      [AnalyticsContext.OfferContext]: offerContext as BaseOfferContext,
      hotelAddress: displayAddress,
      price,
      numberOfNights,
      totalGuests,
      starRating
    });
    setTrackedBookingIds([...trackedBookingIds, bookingId]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, retrieveBookingResult]);
  const room = findRoomByOfferId(rooms || [], sapiMatchedOffer?.id);
  const expiresAt = offerCheckResponse?.expiresAt as string;
  const onCloseOverlay = (e?: React.SyntheticEvent) => {
    if (!errored && e !== undefined) showFrozenDealToast();
    dispatch(closeFreezeConfirmationOverlay());
  };
  useEffect(() => {
    if (!errored && isOpen) {
      // Set a cookie if DealFreeze Confirmation displayed without any errors
      setUserAlreadyFrozeADealCookie();
    }
  }, [errored, isOpen]);
  if (isMobile) return <FreezeOverlay isOpen={isOpen}>
        <OverlayWrapper>
          <ConfirmationContent expiresAt={expiresAt} hotelName={hotelName as string} checkIn={checkIn} checkOut={checkOut} roomsSplit={roomsSplit} room={room} offer={sapiMatchedOffer} imageUrl={imageURIs?.[0] as string} errored={errored} rating={starRating} onCloseOverlay={onCloseOverlay} />
        </OverlayWrapper>

        <CtaButton remainingDeals={data?.remainingFreezes} />
      </FreezeOverlay>;
  return <FreezeOverlay isOpen={isOpen} onClose={onCloseOverlay}>
      <OverlayWrapper className={cx(hasRemainingMessage(data?.remainingFreezes) && '__hasRemainingMessage')}>
        <ConfirmationContent isOverlayContent expiresAt={expiresAt} hotelName={hotelName as string} checkIn={checkIn} checkOut={checkOut} roomsSplit={roomsSplit} room={room} offer={sapiMatchedOffer} imageUrl={imageURIs?.[0] as string} errored={errored} rating={starRating} onCloseOverlay={onCloseOverlay} />
      </OverlayWrapper>

      <CtaButton remainingDeals={data?.remainingFreezes} />
    </FreezeOverlay>;
};

require("./index.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./index.tsx");